import * as React from 'react';
import { memo } from 'react';

import { EventCustomFeedbackCodes, EventFeedbackCodes, EventLevels, EventTypes } from 'defs/events';
import { getRouteHref } from 'defs/routes';
import { HistoryType } from 'types/withRouter';

import Icon from 'components/atoms/Icon';
import appConfig from '../../../helper/mapBackendConfig';

export type HeaderNavLink = {
	id: string;
	text: string;
	icon?: string;
	href?: string;
	selected?: boolean;
	key?: string;
	additions?: Array<any> /* react classes or fns */;
};

export type HeaderNavProps = {
	links: HeaderNavLink[];
	todayDayOfMonth: string;
	linksSignature: string; // Used for memo performance optimisation
	history: HistoryType;
	doAddEvents: (events: any[]) => void;
};

export class HeaderNav extends React.Component<HeaderNavProps> {
	public static displayName = 'HeaderNav';
	public static defaultProps = {
		links: [],
	};

	get currentLocationHref(): string {
		return window.location.href;
	}

	public handleClick = (href: string | void, event: Event) => {
		event.preventDefault();

		if (typeof href !== 'string') {
			return;
		}

		const isInAnamnesis: boolean = this.currentLocationHref.search(getRouteHref('anamnesis')) >= 0;
		const isInLogin: boolean = this.currentLocationHref.search(getRouteHref('login')) >= 0;

		if (isInLogin) {
			this.props.doAddEvents([
				{
					eventLevel: EventLevels.NotAvailable,
					code: EventCustomFeedbackCodes.LoginLeaving,
					eventType: EventTypes.Feedback,
				},
			]);
			return;
		}

		if (isInAnamnesis) {
			this.props.doAddEvents([
				{
					eventLevel: EventLevels.NotAvailable,
					code: EventFeedbackCodes.F12,
					eventType: EventTypes.Feedback,
				},
			]);
			return;
		}

		this.props.history.push(href);
	};

	public renderLinkAdditions(additions?: Array<any>) {
		return additions ? additions.map((Addition: any, index: number) => <Addition key={index} />) : null;
	}

	public renderLink(link: HeaderNavLink, index: number) {
		const { todayDayOfMonth } = this.props;
		const { text, icon, href, selected, additions, id } = link;
		const isToday: boolean = id === 'today' || id === 'today-selected';

		return (
			<li key={index} className="o-header-nav__item">
				<a
					// add baseName for when openening link via right click -> open in new tab
					href={appConfig.env.baseName + href}
					onClick={this.handleClick.bind(this, href) as any}
					className={`o-header-nav__link${selected ? ' state-o-header-nav__link--selected' : ''}`}
				>
					<span className="o-header-nav__text-wrap">
						<div
							className={`o-header-nav__icon-wrapper${
								isToday ? ' o-header-nav__icon-wrapper--today' : ''
							}`}
						>
							{this.renderLinkAdditions(additions)}
							{icon && (
								<span
									className="o-header-nav__icon"
									data-react-additions={additions ? additions.length : 0}
								>
									{' '}
									<Icon iconName={icon} delegate="o-header__trigger" />
								</span>
							)}
							{isToday && <span className="o-header-nav__day">{todayDayOfMonth}</span>}
						</div>
						<span className="o-header-nav__text">{text}</span>
					</span>
				</a>
			</li>
		);
	}

	public render() {
		const { links } = this.props;

		return <ul className="o-header-nav">{links.map(this.renderLink.bind(this))}</ul>;
	}
}

export default memo(HeaderNav, (prevProps, nextProps) => {
	return (
		prevProps.linksSignature === nextProps.linksSignature && prevProps.todayDayOfMonth === nextProps.todayDayOfMonth
	);
});
